import React, { useEffect, ChangeEvent, FocusEvent } from 'react';
import { Form } from 'react-bootstrap';
import { InputFieldState } from '../types';

/**
 * AddressFormコンポーネント用のPropsです。
 * @interface AddressFormProps
 */
interface AddressFormProps {
  /**
   * 住所又は居所
   * @type {InputFieldState}
   * @memberof AddressFormProps
   */
  address: InputFieldState;

  /**
   * 代理人フラグ
   * @type {boolean}
   * @memberof AddressFormProps
   */
  isAgency: boolean;

  /**
   * 必須フラグ
   * @type {boolean}
   * @memberof AddressFormProps
   */
  isRequired: boolean;

  /**
   * 住所又は居所を設定する関数
   * @memberof AddressFormProps
   */
  onChange: (address: InputFieldState) => void;
}

/**
 * 住所又は居所フォームのコンポーネントです。
 * @param {InputFieldState} props.address - 表示する住所又は居所
 * @param {boolean} props.isAgency - 代理人フラグ
 * @param {function} props.onChange - 住所又は居所が変更されたときに呼ばれるコールバック関数
 * @returns {JSX.Element} - 住所又は居所フォームのJSX要素
 */
const AddressForm: React.FC<AddressFormProps> = ({ address, isAgency, isRequired, onChange }) => {

  /**
   * フォームの変更イベントハンドラーです。
   * 入力された住所又は居所をonChange関数を呼び出します。
   * @param {object} event - フォームの変更イベント
   * @returns {void}
   */
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange({
      value: event.target.value,
      errorMessage: address.errorMessage
    });
  };

  /**
   * フォームからフォーカスが外れたときのイベントハンドラーです。
   * 入力された住所又は居所を整形し、onChange関数を呼び出します。
   * @param {object} event - フォーカスアウトのイベント
   * @returns {void}
   */
  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    const value = halfWidthToFullWidth(event.target.value);
    let errorMessage = '';
    if(isRequired && value === '') { 
      // 必須フラグありの場合（代理人の場合は何かしら情報が入力されている、または申出人の場合）は、必ず住所または居所が必要
      errorMessage = '住所又は居所が入力されていません。';
    } else {
      errorMessage = '';
    }

    onChange({
      value: value,
      errorMessage: errorMessage
    });

  }

  /**
   * 半角文字を全角文字に変換する
   * @param {String} inputValue 変換対象の半角文字列
   * @return {String} 全角変換した文字列
   */
  const halfWidthToFullWidth = (inputValue: string) => {
    const fullWidthValue = inputValue.replace(/[A-Za-z0-9]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) + 0xFEE0);
    });

    return fullWidthValue
      .replace(/"/g, "”")
      .replace(/'/g, "’")
      .replace(/`/g, "‘")
      .replace(/\\/g, "￥")
      .replace(/ /g, "　")
      .replace(/~/g, "〜")
      .replace(/-/g, "−");
  };

  return (
    <Form.Group className="mb-2 row">
      <Form.Label className="col-lg-2 col-12 d-flex align-items-center">
        住所又は居所
        {/* 代理人の場合は条件必須、それ以外は必須 */}
        {isAgency ? <span className="text-small text-primary ms-3">※条件必須</span> : <span className="text-small text-danger ms-3">※必須</span>}
      </Form.Label>
      <div className="col-lg-10 col-12">
        <Form.Control
          type="text"
          value={address.value}
          placeholder="住所又は居所を入力してください。"
          onBlur={handleBlur}
          onChange={handleChange}
          aria-label="住所又は居所"
          isInvalid={address.errorMessage !== ''}
        />
        {address.errorMessage !== '' ? <div className="text-end text-danger small">{address.errorMessage}</div> : ''}
      </div>
    </Form.Group>
  );
};

export default AddressForm;